import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { operationApi } from '../../services/api';
import { PackagePlus, Edit, Trash2, AlertTriangle, Boxes, Download, ImageIcon } from 'lucide-react';

const STATUS_OPTIONS = ['available', 'low_stock', 'out_of_stock', 'maintenance'];
const UNIT_OPTIONS = ['pcs', 'liter', 'set', 'pasang', 'unit'];

const statusColors = { available: 'bg-green-100 text-green-700', low_stock: 'bg-yellow-100 text-yellow-700', out_of_stock: 'bg-red-100 text-red-700', maintenance: 'bg-gray-100 text-gray-700' };

export default function OperationInventoryPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', category_id: '', status: '' });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', slug: '', description: '', category: '', inventory_category_id: '',
    quantity: 0, unit: 'pcs', min_stock_level: 5, status: 'available',
    image_url: '', specifications: '', is_active: true,
  });

  const fetchCategories = async () => {
    try {
      const res = await operationApi.inventoryCategories.list({ per_page: 100 });
      const d = res.data.data;
      setCategories(d?.data || d || []);
    } catch (e) { console.error(e); }
  };

  const fetchItems = async (page = 1) => {
    setLoading(true);
    try {
      const params = { per_page: 15, page };
      if (filters.search) params.search = filters.search;
      if (filters.category_id) params.category_id = filters.category_id;
      if (filters.status) params.status = filters.status;
      const res = await operationApi.inventoryItems.list(params);
      const d = res.data.data;
      setItems(d?.data || d || []);
      setPagination({ current_page: d?.current_page || 1, last_page: d?.last_page || 1, total: d?.total || 0 });
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchItems(1); }, [filters]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await operationApi.inventoryItems.update(editing.id, form);
      else await operationApi.inventoryItems.create(form);
      setShowForm(false); setEditing(null);
      resetForm();
      fetchItems(pagination.current_page);
    } catch (err) { alert('Gagal menyimpan: ' + (err.response?.data?.message || '')); } finally { setSaving(false); }
  };

  const resetForm = () => setForm({ name: '', slug: '', description: '', category: '', inventory_category_id: '', quantity: 0, unit: 'pcs', min_stock_level: 5, status: 'available', image_url: '', specifications: '', is_active: true });

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || '', slug: item.slug || '', description: item.description || '',
      category: item.category || '', inventory_category_id: item.inventory_category_id || '',
      quantity: item.quantity || 0, unit: item.unit || 'pcs',
      min_stock_level: item.min_stock_level || 5, status: item.status || 'available',
      image_url: item.image_url || '', specifications: item.specifications || '',
      is_active: item.is_active ?? true,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus item ini?')) return;
    try { await operationApi.inventoryItems.delete(id); fetchItems(pagination.current_page); } catch (err) { alert('Gagal menghapus'); }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (filters.category_id) params.category_id = filters.category_id;
      const res = await operationApi.exportInventoryItems(params);
      const data = res.data.data;
      if (!data || data.length === 0) return alert('Tidak ada data untuk diexport.');
      const header = Object.keys(data[0]);
      const csv = [header.join(','), ...data.map(row => header.map(h => `"${(row[h] ?? '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'inventory-items.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert('Export gagal'); }
  };

  const pages = [];
  for (let i = Math.max(1, pagination.current_page - 2); i <= Math.min(pagination.last_page, pagination.current_page + 2); i++) pages.push(i);

  return (
    <AdminLayout active="Inventaris">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2"><Boxes size={24} /> Inventaris</h1>
          <div className="flex gap-2">
            <button onClick={handleExport} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm flex items-center gap-2"><Download size={16} /> Export CSV</button>
            <button onClick={() => { setEditing(null); resetForm(); setShowForm(true); }} className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark text-sm flex items-center gap-2"><PackagePlus size={16} /> Tambah Item</button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Cari (Nama/Kode)</label>
              <input placeholder="Nama atau kode..." value={filters.search} onChange={e => setFilters({ ...filters, search: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Kategori</label>
              <select value={filters.category_id} onChange={e => setFilters({ ...filters, category_id: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Semua</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Semua</option>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === 'low_stock' ? 'Stok Rendah' : s === 'out_of_stock' ? 'Habis' : s === 'maintenance' ? 'Maintenance' : 'Tersedia'}</option>)}
              </select>
            </div>
          </div>
        </div>

        {loading ? <div className="text-center py-10 text-gray-500">Loading...</div> : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kode</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Stok</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {item.image_url ? <img src={item.image_url} alt={item.name} className="w-10 h-10 rounded-lg object-cover border border-gray-200" onError={e => { e.target.style.display = 'none'; }} /> : <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center"><ImageIcon size={16} className="text-gray-400" /></div>}
                          <div>
                            <p className="text-sm font-medium text-gray-900">{item.name}</p>
                            {item.specifications && <p className="text-[10px] text-gray-400 mt-0.5 max-w-xs truncate">{item.specifications}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className="font-mono text-xs text-navy bg-brand/10 px-2 py-0.5 rounded">{item.item_code || '-'}</span></td>
                      <td className="px-4 py-3 text-xs text-gray-600">{item.category_relation?.icon ? `${item.category_relation.icon} ` : ''}{item.category_relation?.name || item.category || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center">
                          <span className={`text-sm font-bold ${item.quantity <= item.min_stock_level ? 'text-red-600' : 'text-gray-900'}`}>{item.quantity}</span>
                          <span className="text-xs text-gray-400">{item.unit}</span>
                          {item.quantity <= item.min_stock_level && <span className="flex items-center gap-1 text-xs text-red-500 mt-0.5"><AlertTriangle size={10} /> Min: {item.min_stock_level}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${statusColors[item.status] || ''}`}>{item.status === 'low_stock' ? 'Stok Rendah' : item.status === 'out_of_stock' ? 'Habis' : item.status === 'maintenance' ? 'Maintenance' : 'Tersedia'}</span>
                      </td>
                      <td className="px-4 py-3 text-right space-x-1">
                        <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800 text-xs"><Edit size={14} /></button>
                        <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800 text-xs"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Belum ada item inventaris</td></tr>}
                </tbody>
              </table>
            </div>
            {pagination.last_page > 1 && (
              <div className="flex items-center justify-between bg-white rounded-lg shadow px-4 py-3">
                <span className="text-sm text-gray-500">Total {pagination.total} · Halaman {pagination.current_page} dari {pagination.last_page}</span>
                <div className="flex items-center gap-1">
                  <button disabled={pagination.current_page === 1} onClick={() => fetchItems(pagination.current_page - 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30">«</button>
                  {pages.map(p => <button key={p} onClick={() => fetchItems(p)} className={`px-3 py-1 text-sm rounded ${p === pagination.current_page ? 'bg-brand text-white' : 'border border-gray-300'}`}>{p}</button>)}
                  <button disabled={pagination.current_page === pagination.last_page} onClick={() => fetchItems(pagination.current_page + 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30">»</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-lg font-bold text-gray-800">{editing ? 'Edit Item' : 'Tambah Item Baru'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Image preview */}
              {form.image_url && (
                <div className="flex justify-center">
                  <img src={form.image_url} alt="Preview" className="h-24 w-24 object-cover rounded-lg border" onError={e => { e.target.style.display = 'none'; }} />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nama Item *</label>
                  <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Slug *</label>
                  <input type="text" required value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-xs" placeholder="nama-item-slug" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Kategori Inventaris</label>
                  <select value={form.inventory_category_id} onChange={e => setForm({ ...form, inventory_category_id: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">- Pilih -</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === 'low_stock' ? 'Stok Rendah' : s === 'out_of_stock' ? 'Habis' : s === 'maintenance' ? 'Maintenance' : 'Tersedia'}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Jumlah *</label>
                  <input type="number" required min="0" value={form.quantity} onChange={e => setForm({ ...form, quantity: parseInt(e.target.value) || 0 })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
                  <select value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Min Stok</label>
                  <input type="number" min="0" value={form.min_stock_level} onChange={e => setForm({ ...form, min_stock_level: parseInt(e.target.value) || 0 })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">URL Gambar</label>
                  <input type="text" value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="https://..." />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Deskripsi</label>
                <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Spesifikasi</label>
                <textarea rows={2} value={form.specifications} onChange={e => setForm({ ...form, specifications: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Batal</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-brand text-white rounded-lg text-sm hover:bg-brand-dark disabled:opacity-50">{saving ? 'Menyimpan...' : 'Simpan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}